import { t } from '../i18n';
import { verifyVerificationCode } from './auth';

export interface VerificationCodeValidationOptions {
  /** 验证码类型：'login' | 'register' | 'change_password' */
  type: string;
  /** 账号不存在时的回调 */
  onNotExist?: () => void;
  /** 自定义消息解析函数（用于处理嵌套的 messageKey） */
  resolveMessage?: (messageKey?: string | null) => string;
}

export interface VerificationCodeValidationResult {
  isValid: boolean;
  errorMessage?: string;
}

/**
 * 验证验证码格式（前端校验）
 * @param code 验证码
 * @returns 验证结果
 */
export function validateVerificationCodeFormat(code: string): VerificationCodeValidationResult {
  const codeTrimmed = code.trim();

  if (!codeTrimmed) {
    return {
      isValid: false,
      errorMessage: t('common.verificationCode.codeRequired')
    };
  }

  // 验证码必须是6位数字
  if (!/^\d{6}$/.test(codeTrimmed)) {
    return {
      isValid: false,
      errorMessage: t('common.verificationCode.invalid')
    };
  }

  return {
    isValid: true
  };
}

/**
 * 验证验证码（包括格式校验和后端校验）
 * @param countryCode 国家区号（数字，如 86 或 1）
 * @param phone 手机号（纯数字字符串）
 * @param code 验证码
 * @param options 验证选项
 * @returns 验证结果
 */
export async function validateVerificationCode(
  countryCode: number,
  phone: string,
  code: string,
  options: VerificationCodeValidationOptions
): Promise<VerificationCodeValidationResult> {
  // 1. 前端格式校验
  const formatValidation = validateVerificationCodeFormat(code);
  if (!formatValidation.isValid) {
    return formatValidation;
  }

  // 2. 后端校验
  try {
    const verifyResult = await verifyVerificationCode(
      countryCode,
      phone,
      code.trim(),
      options.type
    );

    if (!verifyResult.success) {
      const messageKey = verifyResult.messageKey;

      // 处理账号不存在的情况
      if (messageKey === 'errors.business.account.notExist' && options.onNotExist) {
        options.onNotExist();
        return {
          isValid: false,
          errorMessage: undefined // 已通过回调处理
        };
      }

      // 处理验证码无效的情况，使用通用的验证码无效消息
      if (messageKey === 'errors.business.account.verificationCode.invalid') {
        return {
          isValid: false,
          errorMessage: t('common.verificationCode.invalid')
        };
      }

      // 使用自定义消息解析函数或默认处理
      const message = options.resolveMessage
        ? options.resolveMessage(messageKey)
        : (messageKey ? t(messageKey) : t('common.verificationCode.invalid'));

      return {
        isValid: false,
        errorMessage: message
      };
    }

    return {
      isValid: true
    };
  } catch (error: any) {
    const messageKey = error?.graphQLErrors?.[0]?.extensions?.messageKey;
    const message = options.resolveMessage
      ? options.resolveMessage(messageKey)
      : (messageKey ? t(messageKey) : t('common.verificationCode.invalid'));

    return {
      isValid: false,
      errorMessage: message
    };
  }
}

